/**
 * Nut Sheet types — mirror of lambda/nut-sheet/shared/types.ts.
 *
 * One row per vehicle sales transaction recorded on the daily Nut Sheet.
 */

export type NutSheetCategory = 'NEW' | 'USED';

export interface NutSheetEntry {
  entryId: string;
  locationId: number;
  date: string; // YYYY-MM-DD
  type: string;
  category: NutSheetCategory;

  /** Stock #. Since the 2026-09-01 deal-number swap this is auto-filled by the
   *  deal-number lookup, but stays operator-editable as a manual fallback for
   *  deals not written in DealerBuilt yet. '' = unknown. */
  stockNumber: string;

  /** DealerBuilt deal number (lydeal.dealnumber). Since the 2026-09-01 swap
   *  this is the OPERATOR-ENTERED field that drives the DealerBuilt lookup
   *  (previously lookup-derived from the stock #). null = not entered. */
  dealNumber: string | null;

  soldYear: number | null;
  soldMake: string | null;
  soldModel: string | null;

  // Trade 1 (the original trade fields; auto-populated from the sold deal)
  tradeStockNumber: string | null;
  tradeYear: number | null;
  tradeMake: string | null;
  tradeModel: string | null;
  tradeMiles: number | null;
  tradeComment: string | null; // operator note on trade-1 (e.g. "Sending to Auction")

  // Trade 2 (a deal can have a second trade)
  trade2StockNumber: string | null;
  trade2Year: number | null;
  trade2Make: string | null;
  trade2Model: string | null;
  trade2Miles: number | null;
  trade2Comment: string | null; // operator note on trade-2

  acv: number | null; // trade-1 ACV
  retail: number | string | null; // trade-1 Retail — dollar amount OR free text (e.g. "Auction")
  t1Plan: string | null; // trade-1 resale plan — a USED-type code (e.g., "TCD")
  trade2Acv: number | null;
  trade2Retail: number | string | null; // dollar amount OR free text (e.g. "Auction")
  t2Plan: string | null; // trade-2 resale plan — a USED-type code
  daysInStock: number | null;

  estimatedGross: number | null;
  actualGross: number | null;
  fAndI: number | null;
  points: number | null;
  holdback: number | null;
  doc: number | null;

  financeSource: string | null;       // lender code (auto-filled, operator-overridable)
  financeSourceName: string | null;    // lender company name — shown on hover only

  /** Finance reserve (DealerBuilt lydeal.xtifires) for the sold deal — auto-filled,
   *  not operator-edited. Drives the global Exclude-F&I-Reserve view toggle: when on,
   *  the row's F&I cell + MTD F&I summary show fAndI − financeReserve. */
  financeReserve: number | null;

  /** Open repair orders on the sold unit at lookup time — comma-separated RO
   *  numbers, newest first; null = none open / never looked up. Any rooftop.
   *  Snapshot captured when the stock # is entered (re-entering refreshes it);
   *  not operator-edited. */
  openROs: string | null;
  salesperson: string | null;
  salesperson2: string | null;
  salesManager: string | null;
  fiRep: string | null;
  customerLastName: string | null;

  /** CSI bonus flag — true when the salesperson earns a CSI bonus on this deal
   *  (positive customer survey). Operator-entered via the green CSI toggle in the
   *  Deal Info box; not auto-filled from DealerBuilt. Retail rows only. */
  csiBonus: boolean | null;

  /** Free-text accounting note anchored under Actual Gross. Writable only by the
   *  Accounting and Admin roles; save-entry preserves the stored value for every
   *  other role. Read-only to everyone else; null = no note. */
  accountingComment: string | null;

  /** Free-text deal comment in the blue Deal Info block. Unlike
   *  accountingComment, writable by ANY Nut Sheet user. null = no comment. */
  comment: string | null;

  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;

  /** Optimistic-concurrency version, incremented on each successful write.
   *  Legacy rows written before this field existed report it as absent. */
  version: number;
}

export interface TypeRollup {
  type: string;
  category: NutSheetCategory;
  units: number;
  /**
   * Units excluded from unit counts (swaps — NEW type "S"): their dollars flow
   * into the rollup/totals but they are not sold units, so `units` stays 0 and
   * the real count lands here (rendered in parentheses on the cards). Mirror of
   * lambda/nut-sheet/shared/types.ts.
   */
  excludedUnits?: number;
  gross: number; // PRE-adjustment (Σ Estimated Gross)
  doc: number;
  holdback: number;
  fAndI: number;
  adjustment: number; // Σ (Estimated − Actual) for this type
}

export interface MtdSummary {
  // Retail rollups + totals — EXCLUDE wholesale (USED "W") and value center
  // (USED type ending in "VC"), which live in `wholesale` / `valueCenter`.
  byType: TypeRollup[];
  totals: {
    units: number;
    gross: number;
    doc: number;
    holdback: number;
    fAndI: number;
  };
  adjustments: number;
  // Dealer-to-dealer wholesale (USED type "W") tracked separately; not in byType/totals above.
  wholesale: {
    byType: TypeRollup[];
    totals: {
      units: number;
      gross: number;
      doc: number;
      holdback: number;
      fAndI: number;
    };
    adjustments: number;
  };
  // Value Center (USED type code ending in "VC") tracked separately; not in byType/totals above.
  valueCenter: {
    byType: TypeRollup[];
    totals: {
      units: number;
      gross: number;
      doc: number;
      holdback: number;
      fAndI: number;
    };
    adjustments: number;
  };
}

/**
 * A pre-existing nut-sheet row carrying the same deal #, surfaced by the
 * deal-number lookup so the operator can be warned about a double-entry.
 * Scoped to the same location's month partition.
 */
export interface ExistingNutSheetMatch {
  entryId: string;
  locationId: number;
  date: string; // YYYY-MM-DD
  type: string;
  customerLastName: string | null;
}

/** Deal-number lookup response — mirror of lambda/nut-sheet/shared/types.ts
 *  DealLookupResponse. `stockNumber` is auto-filled from the deal; null when
 *  the deal isn't found. */
export interface DealLookupResponse {
  stockNumber: string | null;
  found: boolean;
  soldYear: number | null;
  soldMake: string | null;
  soldModel: string | null;
  salesperson: string | null;
  salesperson2: string | null;
  salesManager: string | null;
  fiRep: string | null;
  customerLastName: string | null;
  daysInStock: number | null;

  // Finance Source from the sold deal: lender code (cell value) + full company name (hover)
  financeSource: string | null;
  financeSourceName: string | null;

  // Finance reserve (lydeal.xtifires) for the sold deal — auto-filled.
  financeReserve: number | null;

  // Open repair orders on the sold unit (any rooftop) — comma-separated RO
  // numbers, newest first; null = none open.
  openROs: string | null;

  // DealerBuilt deal number (lydeal.dealnumber) — echoes the canonical value
  // when found; null when the deal isn't written in DealerBuilt yet.
  dealNumber: string | null;

  // Trade-in descriptive info (ACV/Retail stay operator-entered, not here)
  tradeStockNumber: string | null;
  tradeYear: number | null;
  tradeMake: string | null;
  tradeModel: string | null;
  tradeMiles: number | null;
  trade2StockNumber: string | null;
  trade2Year: number | null;
  trade2Make: string | null;
  trade2Model: string | null;
  trade2Miles: number | null;

  /** Other rows for the same (location, month) already carrying this deal #. */
  existingEntries: ExistingNutSheetMatch[];
}

export type SaveEntryRequest = Omit<
  NutSheetEntry,
  'entryId' | 'version' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy'
> & {
  entryId?: string;
  /** The version the client last read; lets the server detect a concurrent edit. */
  expectedVersion?: number;
};

export interface ListEntriesResponse {
  entries: NutSheetEntry[];
  mtdSummary: MtdSummary;
}

/**
 * Range-mode response of GET /nut-sheet/entries (startDate + endDate params) —
 * powers the Export to Excel feature. `rangeSummary` covers exactly the entries
 * in [startDate, endDate] (NOT month-to-date). Mirror of
 * lambda/nut-sheet/shared/types.ts.
 */
export interface ListEntriesRangeResponse {
  entries: NutSheetEntry[]; // sorted by date then createdAt
  rangeSummary: MtdSummary;
}

/**
 * Wholesale predicate — frontend mirror of WHOLESALE_TYPE_CODES / isWholesale in
 * lambda/nut-sheet/shared/summarize.ts. A wholesale (dealer-to-dealer) row is a
 * USED entry with type code "W". Keep this in sync with the backend; it drives
 * both the MTD wholesale split and the table's Show/Hide Wholesale toggle.
 */
/** Max length of an accounting comment. Must match ACCOUNTING_COMMENT_MAX in
 *  lambda/nut-sheet/save-entry/index.ts. */
export const ACCOUNTING_COMMENT_MAX = 500;

/** Max length of a deal comment (blue Deal Info block). Must match COMMENT_MAX
 *  in lambda/nut-sheet/save-entry/index.ts. */
export const DEAL_COMMENT_MAX = 500;

export const WHOLESALE_TYPE_CODES = new Set(['W']);

/**
 * Deal # validity — frontend mirror of isNumericDealNumber in
 * lambda/nut-sheet/shared/lookup-match.ts (save-entry 400s anything else;
 * DealerBuilt deal numbers are numeric). Drives DealNumberInput's invalid
 * state and suppresses lookups for invalid input. The raw text is preserved,
 * never sanitized — silently stripping "12O34" to "1234" would look up a
 * DIFFERENT valid deal. Keep in sync with the backend.
 */
export const isNumericDealNumber = (value: string): boolean => /^\d+$/.test(value);

export const isWholesaleRow = (row: Pick<NutSheetEntry, 'category' | 'type'>): boolean =>
  row.category === 'USED' && WHOLESALE_TYPE_CODES.has(row.type);

/**
 * Value Center predicate — frontend mirror of isValueCenter in
 * lambda/nut-sheet/shared/summarize.ts. A Value Center row is a USED entry whose
 * type code ends in "VC" (e.g. CCVC, TDVC). Suffix-based so any future "*VC" code
 * is picked up automatically. Drives both the MTD Value Center split and the
 * table's "Show only Value Center" filter.
 */
export const isValueCenterRow = (row: Pick<NutSheetEntry, 'category' | 'type'>): boolean =>
  row.category === 'USED' && row.type.toUpperCase().endsWith('VC');

/**
 * Swap predicate — a swap (dealer trade) is a NEW entry with type code "S"
 * (label "SWAP"; consistent across all locations). Drives the table's Show/Hide
 * Swap toggle. Server-side (summarizeMonth), swap dollars stay in the retail
 * MTD totals but swap units are excluded from every unit count — the real count
 * comes back as TypeRollup.excludedUnits, shown in parentheses on the cards.
 * Keep in sync with SWAP_TYPE_CODES / isSwap in lambda/nut-sheet/shared/summarize.ts.
 * (Note: USED code "SW", "Stickered W/S", is a different retail type — not a swap.)
 */
export const SWAP_TYPE_CODES = new Set(['S']);

export const isSwapRow = (row: Pick<NutSheetEntry, 'category' | 'type'>): boolean =>
  row.category === 'NEW' && SWAP_TYPE_CODES.has(row.type);

/**
 * Frontend-only row state: an empty draft row that hasn't been saved yet has
 * `entryId = null`. Once persisted, the server assigns a UUID.
 */
export interface NutSheetRowDraft extends Omit<NutSheetEntry, 'entryId' | 'version' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy'> {
  entryId: string | null; // null = draft (unsaved)
  version: number | null; // null = unsaved draft; otherwise the last-known server version
  clientId: string; // stable client-side id; used as React key so first save doesn't remount the row
  isDirty: boolean;

  // --- concurrent-edit reconciliation state (set by the background poll / 409) ---
  /** A newer server copy exists for a row the user is editing. */
  conflict?: boolean;
  /** The current server copy stashed for the "Reload" action. */
  remoteEntry?: NutSheetEntry | null;
  /** The row was deleted by another user while this user was editing it. */
  deletedRemotely?: boolean;
  /** Display name of whoever made the conflicting change. */
  conflictBy?: string | null;
}
